import { RiskAnalysisDto } from '@/api/generated/model';
import { SourceQuote } from '@/app/components/testing-plan/components/test-details/types';
import apiClient from '@/client/api';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';

export type CancerTypeRisk = {
  cancer: TypeOfCancer;
  risk: number;
  averageRisk: number;
  potentialRisk: number;
};

type GetRiskResponse = {
  risk: {
    overallRisk: number;
    risks: CancerTypeRisk[];
  };
  averageRiskPercentage: number;
  potentialRisk: number;
};

export type RiskFactorSummary = {
  riskFactorsCount: number;
  positiveImpactRiskFactorsCount: number;
  negativeImpactRiskFactorsCount: number;
};

export enum RiskFactorType {
  regular = 'regular',
  nonQuantifiablePositive = 'non-quantifiable-positive',
  nonQuantifiableNegative = 'non-quantifiable-negative',
}

export type RiskFactor = {
  id: string;
  label: string;
  whyItMatters: string;
  sources: Array<SourceQuote>;
  shortDescription: string;
  detailedDescription: string;
  impact: number;
  riskFactorType: RiskFactorType;
};

export type RiskFactorsPerCancer = {
  cancerType: TypeOfCancer | 'overall';
  riskFactors: Array<RiskFactor>;
  riskFactorsSummary: RiskFactorSummary;
  modifiableRiskFactorCount: number;
};

export type GetRiskFactorsResponse = {
  factorsPerCancer: Array<RiskFactorsPerCancer>;
};

export type GetRiskFactorsByCancerResponse = {
  factorsPerCancer: Array<RiskFactorsPerCancer>;
};

export type RiskTrendData = {
  label: string;
  risk: number;
  riskChange: number | null;
  averageRisk: number;
  calculatedAt: string;
};

export type GetRiskTrendResponse = {
  userId: string;
  overall: RiskTrendData[];
  perType: {
    [cancerType in TypeOfCancer]: RiskTrendData[];
  };
};

export type GetRiskAnalysisByCancerTypeResponse = RiskAnalysisDto;

async function get(): Promise<GetRiskResponse> {
  const response = await apiClient.get('/risk');
  return response.data;
}

async function riskFactors(): Promise<GetRiskFactorsResponse> {
  const response = await apiClient.get('/risk-factors');
  return response.data;
}

async function riskTrend(): Promise<GetRiskTrendResponse> {
  const response = await apiClient.get('/risk/trend');
  return response.data;
}

async function riskFactorsByCancer(): Promise<GetRiskFactorsByCancerResponse> {
  const response = await apiClient.get('/risk-factors-by-cancer');
  return response.data;
}

async function riskAnalysisByCancerType(cancerType: TypeOfCancer): Promise<GetRiskAnalysisByCancerTypeResponse> {
  const response = await apiClient.get(`/risk/analysis/${cancerType}`);
  return response.data;
}

export default { get, riskFactors, riskTrend, riskFactorsByCancer, riskAnalysisByCancerType };
